import React, { FC, useState } from "react";
import { ProblemBaseBlade } from "common/components/ProblemBlade/ProblemBaseBlade";
import { SelectedProductData } from "common/components/ProductChooser";
import { Box, Text, Button, Input } from "common/components/ui";
import { useIntl } from "react-intl";
import { InvalidEntityData, InvalidEntityIssueType } from "@deliverr/replenishment-client";
import { ProductSearch } from "common/components/ProductSearch";
import { ProductListItem } from "inventory/list/InventoryListReducer";
import { InventoryPool } from "@deliverr/business-types";
import { ProductBladeLabels } from "./ProductBladeLabels";
import { ReplenishmentWarehouseSelectLabels } from "../../replenishment/warehouse-selection/ReplenishmentWarehouseSelectLabels";

export interface ProductDetailsBladeProps {
  product: SelectedProductData | null;
  isOpen: boolean;
  onClose: () => void;
  invalidEntities: InvalidEntityData[];
  onApplyResolution: (
    originalProductDsku: string,
    issueType: InvalidEntityIssueType,
    resolutionData: any,
    originalProduct: SelectedProductData
  ) => void;
  shouldUseCasePack: boolean;
  inventoryPool: InventoryPool;
}

export const ProductDetailsBlade: FC<ProductDetailsBladeProps> = ({
  product,
  isOpen,
  onClose,
  invalidEntities,
  onApplyResolution,
  shouldUseCasePack,
  inventoryPool,
}) => {
  const { formatMessage } = useIntl();

  const [pfnReplacementProduct, setPfnReplacementProduct] = useState<ProductListItem | null>(null);
  const [pfnQty, setPfnQty] = useState<number | string>(product?.qty ?? "");
  const [pfnUnitsPerCase, setPfnUnitsPerCase] = useState<number | string>(product?.caseQty ?? "");
  const [pfnNumCases, setPfnNumCases] = useState<number | string>("");

  const formatIssueType = (issueType: InvalidEntityIssueType): string => {
    switch (issueType) {
      case InvalidEntityIssueType.PRODUCT_NOT_FOUND:
        return formatMessage(ProductBladeLabels.productNotFound);
      case InvalidEntityIssueType.INSUFFICIENT_INVENTORY:
        return formatMessage(ProductBladeLabels.insufficientInventory);
      case InvalidEntityIssueType.CASE_PACK_NOT_FOUND:
        return formatMessage(ProductBladeLabels.casePackNotFound);
      case InvalidEntityIssueType.MULTIPLE_MATCHING_DSKUS:
        return formatMessage(ProductBladeLabels.multipleMatchingDskus);
      case InvalidEntityIssueType.LOT_NOT_FOUND:
        return formatMessage(ProductBladeLabels.lotNotFound);
      case InvalidEntityIssueType.DUPLICATE_ORDER:
        return "Duplicate Order";
      case InvalidEntityIssueType.INVALID_SHIP_BY:
        return "Invalid Ship By Date";
      case InvalidEntityIssueType.INVALID_ADDRESS:
        return "Invalid Address";
      case InvalidEntityIssueType.INVALID_TRANSPORTATION_DETAILS:
        return "Invalid Transportation Details";
      case InvalidEntityIssueType.GENERIC_LINE_ITEM_RESOLUTION_ISSUE:
        return "Generic Line Item Issue";
      default:
        return String(issueType)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  if (!product) {
    return null;
  }

  const handlePfnProductSelect = (selectedProductItem: ProductListItem) => {
    setPfnReplacementProduct(selectedProductItem);
    setPfnQty("");
    setPfnUnitsPerCase(selectedProductItem.raw.caseQty || "");
    setPfnNumCases("");
  };

  const handlePfnApply = (issueType: InvalidEntityIssueType) => {
    const resolutionData: any = {
      newProduct: pfnReplacementProduct,
    };

    // Convert state strings/numbers to numbers for resolutionData
    const numPfnUnitsPerCase = Number(pfnUnitsPerCase);
    const numPfnNumCases = Number(pfnNumCases);
    const numPfnQty = Number(pfnQty);

    if (shouldUseCasePack) {
      resolutionData.unitsPerCase = numPfnUnitsPerCase; // Will be NaN if pfnUnitsPerCase was non-numeric string
      resolutionData.numCases = numPfnNumCases; // Will be NaN if pfnNumCases was non-numeric string
      // Calculated quantity. If inputs are NaN, this could result in NaN.
      resolutionData.quantity = numPfnUnitsPerCase * numPfnNumCases;
    } else {
      resolutionData.quantity = numPfnQty; // Will be NaN if pfnQty was non-numeric string
    }

    onApplyResolution(product.dsku, issueType, resolutionData, product);
    onClose();
  };

  const renderResolutionUI = (issueType: InvalidEntityIssueType, descriptiveText: string) => {
    return (
      <>
        <Box marginBottom="S2">
          <Text size="caption" appearance="DEFAULT" as="div">
            {descriptiveText}
          </Text>
        </Box>
        <Box marginBottom="S3">
          <ProductSearch
            onSelectProduct={handlePfnProductSelect}
            selectedProducts={pfnReplacementProduct ? [pfnReplacementProduct as any] : []}
            placeholder={formatMessage({
              id: "productDetailsBlade.searchPlaceholder",
              defaultMessage: "Search for a replacement product (optional)",
            })}
            autoFocus={false}
            isReserveStorage={inventoryPool === InventoryPool.STORAGE}
            showOnlyPacks={inventoryPool === InventoryPool.STORAGE}
          />
        </Box>
        {pfnReplacementProduct && (
          <div
            style={{ backgroundColor: "#f0f0f0", borderRadius: "4px", marginBottom: "var(--s3)", padding: "var(--s2)" }}
          >
            <Text size="caption" bold>
              Selected Replacement:
            </Text>
            <Text size="caption">
              {pfnReplacementProduct.name} (DSKU: {pfnReplacementProduct.dsku})
            </Text>
          </div>
        )}
        <Box marginBottom="S1">
          <Text size="caption" bold>
            Adjust Quantity/Case Pack (for original or replacement product):
          </Text>
        </Box>
        {shouldUseCasePack ? (
          <>
            <Box marginBottom="S2">
              <Input
                label="Units per Case"
                type="number"
                value={pfnUnitsPerCase.toString()}
                onChange={(e) => setPfnUnitsPerCase(e.target.value)}
              />
            </Box>
            <Box marginBottom="S2">
              <Input
                label="Number of Cases"
                type="number"
                value={pfnNumCases.toString()}
                onChange={(e) => setPfnNumCases(e.target.value)}
              />
            </Box>
          </>
        ) : (
          <Box marginBottom="S2">
            <Input
              label="Quantity"
              type="number"
              value={pfnQty.toString()}
              onChange={(e) => setPfnQty(e.target.value)}
            />
          </Box>
        )}
        <Button onClick={() => handlePfnApply(issueType)}>Apply Changes</Button>
      </>
    );
  };

  const renderResolutionComponent = (issue: InvalidEntityData, prod: SelectedProductData) => {
    const issueTypeDisplay = formatIssueType(issue.issueType);
    let descriptiveText = "";

    switch (issue.issueType) {
      case InvalidEntityIssueType.PRODUCT_NOT_FOUND:
        descriptiveText = formatMessage(ReplenishmentWarehouseSelectLabels.productNotFoundError);
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Box marginBottom="S1">
              <Text size="body" bold>
                {issueTypeDisplay}
              </Text>
            </Box>
            {renderResolutionUI(issue.issueType, descriptiveText)}
          </div>
        );
      case InvalidEntityIssueType.INSUFFICIENT_INVENTORY:
        descriptiveText = formatMessage(ReplenishmentWarehouseSelectLabels.outOfStockProductError);
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Box marginBottom="S1">
              <Text size="body" bold>
                {issueTypeDisplay}
              </Text>
            </Box>
            {renderResolutionUI(issue.issueType, descriptiveText)}
          </div>
        );
      case InvalidEntityIssueType.CASE_PACK_NOT_FOUND: {
        const cpDetails = issue.requestDetails?.[InvalidEntityIssueType.CASE_PACK_NOT_FOUND] as
          | { expectedCasePackQty?: number }
          | undefined;
        const expectedCp = cpDetails?.expectedCasePackQty;
        descriptiveText = formatMessage(ReplenishmentWarehouseSelectLabels.casePackNotFoundError, {
          dsku: prod.dsku,
          expectedCasePackQty: expectedCp,
        });
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Box marginBottom="S1">
              <Text size="body" bold>
                {issueTypeDisplay}
              </Text>
            </Box>
            {renderResolutionUI(issue.issueType, descriptiveText)}
          </div>
        );
      }
      case InvalidEntityIssueType.MULTIPLE_MATCHING_DSKUS:
        descriptiveText = formatMessage(ReplenishmentWarehouseSelectLabels.multipleMatchingDskusError, {
          dsku: prod.dsku,
        });
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Box marginBottom="S1">
              <Text size="body" bold>
                {issueTypeDisplay}
              </Text>
            </Box>
            {renderResolutionUI(issue.issueType, descriptiveText)}
          </div>
        );
      case InvalidEntityIssueType.LOT_NOT_FOUND: {
        const lotDetails = issue.requestDetails?.[InvalidEntityIssueType.LOT_NOT_FOUND] as
          | { lotNumber?: string }
          | undefined;
        descriptiveText = formatMessage(ReplenishmentWarehouseSelectLabels.lotNotFoundError, {
          lotNumber: lotDetails?.lotNumber ?? "provided",
        });
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Box marginBottom="S1">
              <Text size="body" bold>
                {issueTypeDisplay}
              </Text>
            </Box>
            {renderResolutionUI(issue.issueType, descriptiveText)}
          </div>
        );
      }
      default:
        return (
          <div style={{ border: "1px solid #eee", borderRadius: "4px", marginTop: "var(--s2)", padding: "var(--s2)" }}>
            <Text size="body" bold>
              {issueTypeDisplay}
            </Text>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                padding: "var(--s2)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: "150px",
                overflowY: "auto",
              }}
            >
              <pre style={{ margin: 0 }}>{JSON.stringify(issue.requestDetails, null, 2)}</pre>
            </div>
            <Box marginTop="S1">
              <Text size="caption" appearance="DEFAULT" as="div">
                No specific resolution UI for this issue type yet.
              </Text>
            </Box>
          </div>
        );
    }
  };

  return (
    <ProblemBaseBlade
      id={`product-details-blade-${product.dsku}`}
      title={product.name || formatMessage({ id: "productDetailsBlade.title", defaultMessage: "Product Details" })}
      isBladeOpen={isOpen}
      onCloseClick={onClose}
      width="50%"
    >
      <Box>
        <Text as="div" size="bodyLarge" bold>
          {product.name}
        </Text>
        <Box marginBottom="S2">
          <Text as="span" size="body" bold>
            DSKU:{" "}
          </Text>
          <Text as="span" size="body">
            {product.dsku}
          </Text>
        </Box>
        <Box marginBottom="S2">
          <Text as="span" size="body" bold>
            MSKU:{" "}
          </Text>
          <Text as="span" size="body">
            {product.msku ?? "-"}
          </Text>
        </Box>
        {shouldUseCasePack ? (
          <>
            <Box marginBottom="S2">
              <Text as="span" size="body" bold>
                Units per Case:{" "}
              </Text>
              <Text as="span" size="body">
                {product.caseQty ?? "N/A"}
              </Text>
            </Box>
            <Box marginBottom="S2">
              <Text as="span" size="body" bold>
                Number of Cases:{" "}
              </Text>
              <Text as="span" size="body">
                {product.caseQty && product.qty && product.caseQty !== 0 ? product.qty / product.caseQty : "N/A"}
              </Text>
            </Box>
            <Box marginBottom="S2">
              <Text as="span" size="body" bold>
                Total Quantity:{" "}
              </Text>
              <Text as="span" size="body">
                {product.qty}
              </Text>
            </Box>
          </>
        ) : (
          <Box marginBottom="S2">
            <Text as="span" size="body" bold>
              Quantity in Order:{" "}
            </Text>
            <Text as="span" size="body">
              {product.qty}
            </Text>
          </Box>
        )}

        {invalidEntities && invalidEntities.length > 0 && (
          <div style={{ borderTop: "1px solid #ccc", marginTop: "var(--s4)", paddingTop: "var(--s3)" }}>
            <Box marginBottom="S3">
              <Text as="div" size="bodyLarge" bold>
                Resolution Required
              </Text>
            </Box>
            {invalidEntities.map((issue) => (
              <Box key={`${issue.invalidEntityId}-${issue.issueType}`} marginBottom="S3">
                {renderResolutionComponent(issue, product)}
              </Box>
            ))}
          </div>
        )}
      </Box>
    </ProblemBaseBlade>
  );
};
